import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  type SignInInput,
  type SignUpInput,
  type ConfirmSignUpInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput
} from 'aws-amplify/auth';

// Dev bypass for local testing without Cognito
const DEV_BYPASS_AUTH = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

// Manual skip: lets a user get past a broken/unconfigured login without real
// account data. Persisted so it survives app restarts until they log out.
const SKIP_AUTH_KEY = 'factory_skip_auth';
const skipLoginEnabled = () => localStorage.getItem(SKIP_AUTH_KEY) === 'true';

const LOCAL_USER: User = {
  username: 'local',
  userId: 'local-skip-auth',
};

interface User {
  username: string;
  userId: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<{ needsConfirmation: boolean }>;
  confirmRegistration: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  skipLogin: () => void;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  getAuthToken: () => Promise<string | null>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const checkUser = useCallback(async () => {
    // Dev bypass - skip Cognito auth for local testing
    if (DEV_BYPASS_AUTH) {
      console.log('[Auth] Dev bypass enabled - using mock user');
      setUser({
        username: 'ruud',
        userId: 'dev-ruud-cryptohopper',
        email: 'ruud@cryptohopper.com',
      });
      setIsLoading(false);
      return;
    }

    if (skipLoginEnabled()) {
      setUser(LOCAL_USER);
      setIsLoading(false);
      return;
    }

    try {
      const currentUser = await getCurrentUser();

      setUser({
        username: currentUser.username,
        userId: currentUser.userId,
        email: currentUser.signInDetails?.loginId,
      });
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUser();
  }, [checkUser]);

  const login = async (username: string, password: string) => {
    setError(null);
    try {
      const result = await signIn({ username, password });
      
      if (result.isSignedIn) {
        await checkUser();
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        const confirmResult = await confirmSignIn({ challengeResponse: password });
        if (confirmResult.isSignedIn) {
          await checkUser();
        }
      } else if (result.nextStep?.signInStep === 'DONE') {
        await checkUser();
      } else {
        await checkUser();
      }
    } catch (err: any) {
      const message = err.message || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (email: string, password: string) => {
    setError(null);
    try {
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      return { needsConfirmation: !result.isSignUpComplete };
    } catch (err: any) {
      const message = err.message || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  };

  const confirmRegistration = async (email: string, code: string) => {
    setError(null);
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
    } catch (err: any) {
      const message = err.message || 'Confirmation failed';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    localStorage.removeItem(SKIP_AUTH_KEY);
    try {
      await signOut();
    } catch (err: any) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
    }
  };

  const skipLogin = () => {
    localStorage.setItem(SKIP_AUTH_KEY, 'true');
    setUser(LOCAL_USER);
  };

  const forgotPassword = async (email: string) => {
    setError(null);
    try {
      await resetPassword({ username: email });
    } catch (err: any) {
      const message = err.message || 'Password reset request failed';
      setError(message);
      throw new Error(message);
    }
  };

  const confirmForgotPassword = async (email: string, code: string, newPassword: string) => {
    setError(null);
    try {
      await confirmResetPassword({ 
        username: email, 
        confirmationCode: code, 
        newPassword 
      });
    } catch (err: any) {
      const message = err.message || 'Password reset failed';
      setError(message);
      throw new Error(message);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        confirmRegistration,
        logout,
        skipLogin,
        forgotPassword,
        confirmForgotPassword,
        getAuthToken,
        error,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export async function getAuthToken(): Promise<string | null> {
  // Dev bypass - return null so backend uses its own dev mode
  // Backend with FACTORY_DEV_MODE=true will allow unauthenticated requests
  if (DEV_BYPASS_AUTH || skipLoginEnabled()) {
    return null;
  }

  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() || null;
  } catch {
    return null;
  }
}
